import {
  Badge,
  Button,
  cn,
  confirm,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormInput,
  FormNumber,
  FormPrice,
  FormRoot,
  RHF,
  Skeleton,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangleIcon,
  DollarSignIcon,
  FilePlusIcon,
  FileTextIcon,
  PencilIcon,
  PlusIcon,
  RefreshCcwIcon,
  TrashIcon,
  XIcon,
} from "lucide-react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  DOCUMENT_CURRENCIES,
  DOCUMENT_CURRENCY_SYMBOL,
  type DocumentCurrency,
  formatDocumentMoney,
  toMajorUnits,
} from "@/shared/lib/performance-document-currency";
import {
  defaultDocumentPrintSignatureOptions,
  type DocumentPrintSignatureOptions,
} from "@/shared/lib/document-print-signature-options";
import {
  installmentPlanTargetAmount,
  isLegacyTaxInclusiveInstallmentPlan,
  resolveInstallmentPrintAmountExTax,
} from "@/shared/lib/performance-document-payment-terms";
import type {
  AssignmentDTO,
  CreateManualLineDTO,
  DocumentDTO,
  DocumentLineDTO,
  PricingSyncResultDTO,
} from "../../domain/contracts";
import { performanceApi } from "../api/client";
import {
  eventsKeys,
  useCreateManualLine,
  useCreatePricingDraft,
  useDeleteDocument,
  useDeleteManualLine,
  useDocumentQuery,
  useInvoicePaymentSummary,
  useQuotationInstallmentPlan,
  useReopenDocument,
  useSyncPricingFromAssignments,
  useUpdateDocumentCurrency,
  useUpdateManualLine,
  useVoidDocument,
} from "../api/queries";
import { CreateInvoiceFromQuotationDialog } from "./CreateInvoiceFromQuotationDialog";
import { DocumentBankAccountEditor } from "./DocumentBankAccountEditor";
import { DocumentInstallmentLinesTable } from "./DocumentInstallmentLinesTable";
import { DocumentPaymentTermsEditor } from "./DocumentPaymentTermsEditor";
import { DocumentPrintSignatureOptionsPanel } from "./DocumentPrintSignatureOptionsPanel";
import { DocumentTaxEditor,
  type DocumentTaxEditorHandle,
} from "./DocumentTaxEditor";
import { PricingSectionSkeleton } from "./EventDetailSectionSkeletons";
import { InvoicePaymentPanel } from "./InvoicePaymentPanel";
import { IssueDocumentDialog } from "./IssueDocumentDialog";
import { PerformanceBrandPrintMenu } from "./PerformanceBrandPrintMenu";
import { QuotationInstallmentPlanPanel } from "./QuotationInstallmentPlanPanel";
import { RoleFeePricingBreakdownPanel } from "./RoleFeePricingBreakdownPanel";

const ManualLineFormSchema = z.object({
  description: z.string().min(1, "ຕ້ອງປ້ອນລາຍລະອຽດ"),
  quantity: z.number().int().min(1),
  unit: z.string().nullable().optional(),
  unitPrice: z.number().min(0),
  discountAmount: z.number().min(0).optional(),
});

type ManualLineFormValues = z.infer<typeof ManualLineFormSchema>;

const AutoLinePricingFormSchema = z.object({
  unitPrice: z.number().min(0),
  discountAmount: z.number().min(0).optional(),
});
type AutoLinePricingFormValues = z.infer<typeof AutoLinePricingFormSchema>;

const LINE_TYPE_LABELS = {
  auto: "ອັດຕະໂນມັດ",
  manual: "ເພີ່ມເອງ",
} as const;

const DOCUMENT_TYPE_LABELS = {
  quotation: "ໃບສະເໜີລາຄາ",
  invoice: "ໃບເກັບເງິນ",
  receipt: "ໃບຮັບເງິນ",
} as const;

const DOCUMENT_STATUS_LABELS = {
  draft: "ຮ່າງ",
  pending_approval: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  issued: "ອອກແລ້ວ",
  partial: "ຊຳລະບາງສ່ວນ",
  paid: "ຊຳລະແລ້ວ",
  voided: "ຍົກເລີກ",
} as const;

const DOCUMENT_STATUS_VARIANTS = {
  draft: "secondary",
  pending_approval: "secondary",
  approved: "default",
  issued: "outline",
  partial: "secondary",
  paid: "default",
  voided: "destructive",
} as const;

const UNIT_LABELS: Record<string, string> = {
  hour: "ຊົ່ວໂມງ",
  show: "ໂຊ",
  day: "ວັນ",
  item: "ລາຍການ",
};

function formatMoney(value: number, currency: DocumentCurrency = "LAK") {
  return formatDocumentMoney(value, currency);
}

function currencySymbol(currency: DocumentCurrency = "LAK") {
  return DOCUMENT_CURRENCY_SYMBOL[currency];
}

function displayUnit(unit: string | null) {
  if (!unit) return "";
  return UNIT_LABELS[unit] ?? unit;
}

function pickDefaultDocumentId(documents: DocumentDTO[]): string | null {
  if (documents.length === 0) return null;
  return (
    documents.find(
      (doc) => doc.documentType === "quotation" && doc.status === "draft",
    )?.id ??
    documents.find((doc) => doc.status === "draft")?.id ??
    documents[0]?.id ??
    null
  );
}

type ManualLineDialogState =
  | { open: false }
  | { open: true; mode: "create" }
  | { open: true; mode: "edit"; line: DocumentLineDTO };

const MANUAL_LINE_DEFAULT_VALUES: ManualLineFormValues = {
  description: "",
  quantity: 1,
  unit: "ລາຍການ",
  unitPrice: 0,
  discountAmount: 0,
};

function ManualLineDialog({
  state,
  onOpenChange,
  submitting,
  onSubmit,
  manualBilling = false,
  currency = "LAK",
}: {
  state: ManualLineDialogState;
  onOpenChange: (open: boolean) => void;
  submitting?: boolean;
  onSubmit: (values: CreateManualLineDTO) => void | Promise<void>;
  manualBilling?: boolean;
  currency?: DocumentCurrency;
}) {
  const open = state.open;
  const mode = state.open ? state.mode : "create";
  const line = state.open && state.mode === "edit" ? state.line : null;
  const symbol = currencySymbol(currency);

  const methods = RHF.useForm<ManualLineFormValues>({
    resolver: zodResolver(ManualLineFormSchema),
    defaultValues: MANUAL_LINE_DEFAULT_VALUES,
  });

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && line) {
      methods.reset({
        description: line.description,
        quantity: line.quantity,
        unit: line.unit,
        unitPrice: toMajorUnits(line.unitPrice, currency),
        discountAmount: toMajorUnits(line.discountAmount ?? 0, currency),
      });
      return;
    }
    methods.reset(MANUAL_LINE_DEFAULT_VALUES);
  }, [open, mode, line, methods, currency]);

  const title = mode === "create" ? "ເພີ່ມລາຍການເອງ" : "ແກ້ໄຂລາຍການເອງ";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {manualBilling
              ? "ເພີ່ມລາຍການລາຄາສຳລັບງານ Event — ທຸກບັນທັດເພີ່ມດ້ວຍມື"
              : "ເພີ່ມລາຍການລາຄາດ້ວຍມືໃນເອກະສານ"}
          </DialogDescription>
        </DialogHeader>
        <FormRoot
          methods={methods}
          onSubmit={(values) => onSubmit(values as CreateManualLineDTO)}
          className="grid gap-4 sm:grid-cols-2"
        >
          <div className="sm:col-span-2">
            <FormInput
              name="description"
              label="ລາຍລະອຽດ"
              placeholder="ຕົວຢ່າງ: ຄ່າເດີນທາງ"
            />
          </div>
          <FormNumber name="quantity" label="ຈຳນວນ" min={1} />
          <FormInput name="unit" label="ໜ່ວຍ" placeholder="ລາຍການ" />
          <FormPrice
            name="unitPrice"
            label="ລາຄາຕໍ່ໜ່ວຍ"
            min={0}
            currencySymbol={symbol}
          />
          <FormPrice
            name="discountAmount"
            label="ສ່ວນຫຼຸດ"
            min={0}
            currencySymbol={symbol}
          />
          <DialogFooter className="sm:col-span-2">
            <Button
              type="button"
              variant="outline"
              disabled={submitting}
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "ກຳລັງບັນທຶກ..." : mode === "create" ? "ເພີ່ມ" : "ບັນທຶກ"}
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}

function AutoLinePricingForm({
  line,
  submitting,
  onSubmit,
  onCancel,
  currency = "LAK",
}: {
  line: DocumentLineDTO;
  submitting?: boolean;
  onSubmit: (values: AutoLinePricingFormValues) => void;
  onCancel: () => void;
  currency?: DocumentCurrency;
}) {
  const symbol = currencySymbol(currency);
  const methods = RHF.useForm<AutoLinePricingFormValues>({
    resolver: zodResolver(AutoLinePricingFormSchema),
    defaultValues: {
      unitPrice: toMajorUnits(line.unitPrice, currency),
      discountAmount: toMajorUnits(line.discountAmount ?? 0, currency),
    },
  });

  return (
    <div className="rounded-lg border bg-muted/20">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div>
          <p className="font-semibold text-sm">ແກ້ໄຂລາຄາ: {line.description}</p>
          <p className="text-muted-foreground text-xs">
            ຈຳນວນ {line.quantity} {displayUnit(line.unit)} · ລາຍການອັດຕະໂນມັດ
          </p>
        </div>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          disabled={submitting}
          onClick={onCancel}
        >
          <XIcon className="size-4" />
        </Button>
      </div>
      <FormRoot
        methods={methods}
        onSubmit={(values) => onSubmit(values)}
        className="grid items-start gap-3 p-4 md:grid-cols-[minmax(180px,1fr)_minmax(180px,1fr)_96px]"
      >
        <FormPrice
          name="unitPrice"
          label="ລາຄາຕໍ່ໜ່ວຍ"
          min={0}
          currencySymbol={symbol}
        />
        <FormPrice
          name="discountAmount"
          label="ສ່ວນຫຼຸດ"
          min={0}
          currencySymbol={symbol}
        />
        <div className="pt-[22px]">
          <Button type="submit" disabled={submitting} className="h-10 w-full">
            {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
        </div>
      </FormRoot>
    </div>
  );
}

export function PricingSection({
  eventId,
  assignments = [],
  eventShows,
  hasPerformers = true,
}: {
  eventId: string;
  assignments?: AssignmentDTO[];
  eventShows?: import("../../domain/contracts").EventShowDTO[];
  hasPerformers?: boolean;
}) {
  const isManualBilling = !hasPerformers;
  const canReadDocuments = useActionPermission(["performance-documents:read"]);
  const canCreateDocument = useActionPermission([
    "performance-documents:create",
  ]);
  const canIssueDocument = useActionPermission(["performance-documents:issue"]);
  const canVoidDocument = useActionPermission(["performance-documents:void"]);
  const canDeleteDocument = useActionPermission([
    "performance-documents:delete",
  ]);
  const canCreatePricing = useActionPermission(["performance-pricing:create"]);
  const canUpdatePricing = useActionPermission(["performance-pricing:update"]);
  const canDeletePricing = useActionPermission(["performance-pricing:delete"]);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null,
  );
  const [draftCurrency, setDraftCurrency] = useState<DocumentCurrency>("LAK");
  const [manualLineDialog, setManualLineDialog] =
    useState<ManualLineDialogState>({ open: false });
  const [editingLine, setEditingLine] = useState<DocumentLineDTO | null>(null);
  const [syncWarnings, setSyncWarnings] = useState<
    PricingSyncResultDTO["warnings"]
  >([]);
  const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false);
  const [issueDocumentTarget, setIssueDocumentTarget] =
    useState<DocumentDTO | null>(null);
  const [printSignatureOptions, setPrintSignatureOptions] =
    useState<DocumentPrintSignatureOptions>(() =>
      defaultDocumentPrintSignatureOptions("quotation", "draft"),
    );
  const documentTaxEditorRef = useRef<DocumentTaxEditorHandle>(null);
  const documentsQuery = useQuery({
    queryKey: eventsKeys.documents(eventId),
    queryFn: () => performanceApi.events.documents.list(eventId),
    enabled: !!eventId,
  });
  const documents = documentsQuery.data ?? [];

  const hasActiveDraftQuotation = useMemo(
    () =>
      documents.some(
        (doc) => doc.documentType === "quotation" && doc.status === "draft",
      ),
    [documents],
  );

  useEffect(() => {
    if (documents.length === 0) {
      setSelectedDocumentId(null);
      return;
    }
    if (
      selectedDocumentId &&
      documents.some((doc) => doc.id === selectedDocumentId)
    ) {
      return;
    }
    setSelectedDocumentId(pickDefaultDocumentId(documents));
  }, [documents, selectedDocumentId]);

  const selectedSummary = useMemo(
    () => documents.find((doc) => doc.id === selectedDocumentId) ?? null,
    [documents, selectedDocumentId],
  );

  const documentQuery = useDocumentQuery(selectedDocumentId);
  const document = documentQuery.data ?? selectedSummary ?? null;

  useEffect(() => {
    if (!document) return;
    setPrintSignatureOptions(
      defaultDocumentPrintSignatureOptions(
        document.documentType,
        document.status,
      ),
    );
  }, [document?.id, document?.documentType, document?.status]);

  const editableDocument =
    document?.status === "draft" && document.documentType === "quotation"
      ? document
      : null;
  const paymentTermsDocument =
    document?.status === "draft" &&
    (document.documentType === "quotation" ||
      document.documentType === "invoice")
      ? document
      : null;
  const bankAccountDocument =
    document?.status === "draft" && document.documentType === "invoice"
      ? document
      : null;
  const lines = document?.lines ?? [];
  const autoCount = lines.filter((line) => line.lineType === "auto").length;
  const manualCount = lines.filter((line) => line.lineType === "manual").length;

  const showQuotationLineItems =
    document?.documentType === "quotation" ||
    (document?.documentType === "invoice" && document.status === "draft");

  const showInstallmentLineItems =
    (document?.documentType === "invoice" &&
      document.status !== "draft" &&
      document.status !== "voided") ||
    document?.documentType === "receipt";

  const invoiceIdForInstallments =
    document?.documentType === "invoice"
      ? document.id
      : document?.documentType === "receipt"
        ? document.parentDocumentId
        : null;

  const installmentSummaryQuery = useInvoicePaymentSummary(
    eventId,
    showInstallmentLineItems ? invoiceIdForInstallments : null,
  );

  const installmentLineItems = useMemo(() => {
    if (!showInstallmentLineItems || !document) return [];
    const allInstallments = installmentSummaryQuery.data?.installments ?? [];
    if (document.documentType === "receipt") {
      if (document.installmentId) {
        const linked = allInstallments.find(
          (row) => row.id === document.installmentId,
        );
        return linked ? [linked] : [];
      }
      const description =
        lines.find((line) => line.lineCategory === "revenue")?.description ??
        "ຮັບຊຳລະ";
      return [
        {
          id: `receipt-line-${document.id}`,
          invoiceId: document.parentDocumentId ?? "",
          installmentNo: 1,
          label: description,
          labelEn: null,
          dueDate: document.issuedAt,
          amount: document.grandTotal,
          receiptId: document.id,
          status: "paid" as const,
          sortOrder: 0,
          createdAt: document.createdAt,
          updatedAt: document.updatedAt,
        },
      ];
    }
    const planSum = allInstallments.reduce((sum, row) => sum + row.amount, 0);
    const legacyInclusive = isLegacyTaxInclusiveInstallmentPlan(planSum, document);
    const taxContext = {
      grandTotal: document.grandTotal,
      taxAmount: document.taxAmount ?? 0,
      taxEnabled: document.taxEnabled ?? false,
    };
    return allInstallments.map((row) => ({
      ...row,
      amount: legacyInclusive
        ? resolveInstallmentPrintAmountExTax(row.amount, taxContext)
        : row.amount,
    }));
  }, [
    document,
    installmentSummaryQuery.data?.installments,
    lines,
    showInstallmentLineItems,
  ]);

  const installmentPlanTargetMinor =
    document && showInstallmentLineItems && document.documentType === "invoice"
      ? installmentPlanTargetAmount(document)
      : undefined;

  const createDraft = useCreatePricingDraft(eventId);
  const syncPricing = useSyncPricingFromAssignments(eventId);
  const updateCurrency = useUpdateDocumentCurrency(
    eventId,
    editableDocument?.id ?? "",
  );
  const reopenDocument = useReopenDocument(eventId);
  const voidDocument = useVoidDocument(eventId);
  const deleteDocument = useDeleteDocument(eventId);
  const createManualLine = useCreateManualLine(
    eventId,
    editableDocument?.id ?? "",
  );
  const updateManualLine = useUpdateManualLine(
    eventId,
    editableDocument?.id ?? "",
  );
  const deleteManualLine = useDeleteManualLine(
    eventId,
    editableDocument?.id ?? "",
  );
  const issuedQuotation = documents.find(
    (item) => item.documentType === "quotation" && item.status === "issued",
  );
  const issuedQuotationPlanQuery = useQuotationInstallmentPlan(
    eventId,
    issuedQuotation?.id ?? null,
  );
  const hasQuotationInstallmentPlan =
    (issuedQuotationPlanQuery.data?.installments.length ?? 0) > 0;
  const hasUninvoicedQuotationInstallments = (
    issuedQuotationPlanQuery.data?.installments ?? []
  ).some((row) => !row.invoicedInvoiceId);
  const canCreateInvoiceFromQuotation =
    Boolean(issuedQuotation) &&
    hasQuotationInstallmentPlan &&
    hasUninvoicedQuotationInstallments &&
    canCreateDocument;

  function handleCreateDraftQuotation() {
    createDraft.mutate(
      { currency: draftCurrency },
      {
        onSuccess: (doc) => setSelectedDocumentId(doc.id),
      },
    );
  }

  function closePricingEditors() {
    setManualLineDialog({ open: false });
    setEditingLine(null);
  }

  async function savePendingDocumentTaxChanges() {
    await documentTaxEditorRef.current?.savePendingChanges();
  }

  function selectDocument(documentId: string) {
    setSelectedDocumentId(documentId);
    setSyncWarnings([]);
    closePricingEditors();
  }

  const showInvoicePayments =
    selectedSummary?.documentType === "invoice" &&
    selectedSummary.status !== "voided" &&
    selectedSummary.status !== "draft" &&
    ["issued", "partial", "paid"].includes(selectedSummary.status);

  if (!canReadDocuments) return null;

  if (documentsQuery.isLoading && documents.length === 0) {
    return <PricingSectionSkeleton />;
  }

  return (
    <section
      id="event-detail-pricing-panel"
      className="mt-6 rounded-xl border bg-card"
      aria-labelledby="event-pricing-heading"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <DollarSignIcon className="size-4" />
          </div>
          <div className="min-w-0">
            <h2 id="event-pricing-heading" className="font-semibold text-base">
              ລາຄາ / ເອກະສານລູກຄ້າ
            </h2>
            <p className="text-muted-foreground text-xs">
              {isManualBilling
                ? "ງານ Event — ເພີ່ມລາຍການດ້ວຍມືໃນໃບສະເໜີລາຄາ"
                : "ເລືອກເອກະສານເພື່ອເບິ່ງລາຍລະອຽດ ແລະ ຈັດການ"}
            </p>
          </div>
        </div>
        {!hasActiveDraftQuotation && canCreateDocument && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex overflow-hidden rounded-md border">
              {DOCUMENT_CURRENCIES.map((code) => (
                <button
                  key={code}
                  type="button"
                  className={cn(
                    "px-2.5 py-1.5 text-xs font-medium transition-colors",
                    draftCurrency === code
                      ? "bg-primary text-primary-foreground"
                      : "bg-background text-muted-foreground hover:bg-muted",
                  )}
                  onClick={() => setDraftCurrency(code)}
                >
                  {code}
                </button>
              ))}
            </div>
            <Button
              size="sm"
              disabled={createDraft.isPending}
              onClick={handleCreateDraftQuotation}
            >
              <FileTextIcon className="size-4" />
              {createDraft.isPending
                ? "ກຳລັງສ້າງ..."
                : documents.length === 0
                  ? "ສ້າງເອກະສານຮ່າງລາຄາ"
                  : "ສ້າງຮ່າງໃບສະເໜີລາຄາໃໝ່"}
            </Button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-[minmax(260px,320px)_minmax(0,1fr)] lg:divide-x">
        <aside className="border-b bg-muted/10 p-4 lg:border-b-0">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="font-semibold text-sm">ເອກະສານ</p>
            <div className="flex flex-wrap items-center gap-2">
              {!hasActiveDraftQuotation && canCreateDocument && (
                <>
                  <div className="flex overflow-hidden rounded-md border">
                    {DOCUMENT_CURRENCIES.map((code) => (
                      <button
                        key={code}
                        type="button"
                        className={cn(
                          "px-2 py-1 text-[11px] font-medium transition-colors",
                          draftCurrency === code
                            ? "bg-primary text-primary-foreground"
                            : "bg-background text-muted-foreground hover:bg-muted",
                        )}
                        onClick={() => setDraftCurrency(code)}
                      >
                        {code}
                      </button>
                    ))}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={createDraft.isPending}
                    onClick={handleCreateDraftQuotation}
                  >
                    <FilePlusIcon className="size-4" />
                    {createDraft.isPending ? "ກຳລັງສ້າງ..." : "ຮ່າງ QT"}
                  </Button>
                </>
              )}
              {canCreateInvoiceFromQuotation && issuedQuotation && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCreateInvoiceOpen(true)}
                >
                  <FilePlusIcon className="size-4" />
                  ສ້າງໃບເກັບເງິນ
                </Button>
              )}
            </div>
          </div>

          {documentsQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-20 w-full rounded-lg" />
              <Skeleton className="h-20 w-full rounded-lg" />
            </div>
          ) : documents.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              ຍັງບໍ່ມີເອກະສານ — ສ້າງຮ່າງໃບສະເໜີລາຄາກ່ອນ
            </p>
          ) : (
            <div className="space-y-2">
              {documents.map((item) => {
                const isSelected = item.id === selectedDocumentId;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => selectDocument(item.id)}
                    className={cn(
                      "w-full rounded-lg border p-3 text-left transition-colors",
                      isSelected
                        ? "border-primary bg-primary/5"
                        : "border-border bg-background hover:bg-muted/40",
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-medium text-sm">
                        {DOCUMENT_TYPE_LABELS[item.documentType]}
                      </span>
                      <Badge
                        variant={
                          DOCUMENT_STATUS_VARIANTS[item.status] ?? "outline"
                        }
                      >
                        {DOCUMENT_STATUS_LABELS[item.status] ?? item.status}
                      </Badge>
                    </div>
                    <p className="mt-1 text-muted-foreground text-xs">
                      {item.documentNo}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px]">
                        {item.currency ?? "LAK"}
                      </Badge>
                      <p className="font-semibold text-sm tabular-nums">
                        {formatMoney(item.grandTotal, item.currency ?? "LAK")}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </aside>

        <div className="min-w-0 p-5">
          {documentsQuery.isLoading || documentQuery.isLoading ? (
            <div
              className="space-y-4"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <Skeleton className="h-14 w-full rounded-lg" />
              <div className="grid gap-3 md:grid-cols-3">
                <Skeleton className="h-20 rounded-lg" />
                <Skeleton className="h-20 rounded-lg" />
                <Skeleton className="h-20 rounded-lg" />
              </div>
              <Skeleton className="h-48 w-full rounded-lg" />
            </div>
          ) : !document ? (
            <div className="py-10 text-center">
              <FileTextIcon className="mx-auto mb-3 size-8 text-muted-foreground" />
              <p className="font-medium">
                {hasActiveDraftQuotation
                  ? "ເລືອກເອກະສານຈາກລາຍການດ້ານຊ້າຍ"
                  : "ຍັງບໍ່ມີເອກະສານຮ່າງລາຄາ"}
              </p>
              <p className="mt-1 text-muted-foreground text-sm">
                {hasActiveDraftQuotation
                  ? "ກົດເອກະສານເພື່ອເບິ່ງລາຍລະອຽດ"
                  : isManualBilling
                    ? "ສ້າງໃບສະເໜີລາຄາ ແລ້ວເພີ່ມລາຍການດ້ວຍມື"
                    : "ສ້າງເອກະສານຮ່າງ ແລ້ວເພີ່ມລາຍການລາຄາ"}
              </p>
              {!hasActiveDraftQuotation && canCreateDocument ? (
                <div className="mt-4 flex flex-col items-center gap-3">
                  <div className="flex overflow-hidden rounded-md border">
                    {DOCUMENT_CURRENCIES.map((code) => (
                      <button
                        key={code}
                        type="button"
                        className={cn(
                          "px-2.5 py-1.5 text-xs font-medium transition-colors",
                          draftCurrency === code
                            ? "bg-primary text-primary-foreground"
                            : "bg-background text-muted-foreground hover:bg-muted",
                        )}
                        onClick={() => setDraftCurrency(code)}
                      >
                        {code}
                      </button>
                    ))}
                  </div>
                  <Button
                    size="sm"
                    disabled={createDraft.isPending}
                    onClick={handleCreateDraftQuotation}
                  >
                    <FileTextIcon className="size-4" />
                    {createDraft.isPending ? "ກຳລັງສ້າງ..." : "ສ້າງເອກະສານຮ່າງລາຄາ"}
                  </Button>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border bg-muted/20 px-4 py-3">
                <div>
                  <p className="font-semibold">
                    {DOCUMENT_TYPE_LABELS[document.documentType]} ·{" "}
                    {document.documentNo}
                  </p>
                  <p className="mt-1 text-muted-foreground text-xs">
                    {showInstallmentLineItems
                      ? document.documentType === "receipt"
                        ? "ລາຍການງວດທີ່ຮັບຊຳລະ"
                        : `${installmentLineItems.length} ງວດ`
                      : isManualBilling
                        ? `${manualCount} ລາຍການເພີ່ມເອງ`
                        : `${autoCount} ອັດຕະໂນມັດ · ${manualCount} ເພີ່ມເອງ`}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    variant={
                      DOCUMENT_STATUS_VARIANTS[document.status] ?? "outline"
                    }
                  >
                    {DOCUMENT_STATUS_LABELS[document.status] ?? document.status}
                  </Badge>
                  {editableDocument && canCreateDocument ? (
                    <div className="flex overflow-hidden rounded-md border">
                      {DOCUMENT_CURRENCIES.map((code) => (
                        <button
                          key={code}
                          type="button"
                          disabled={
                            updateCurrency.isPending ||
                            (document.currency ?? "LAK") === code
                          }
                          className={cn(
                            "px-2 py-1 text-[11px] font-medium transition-colors disabled:opacity-60",
                            (document.currency ?? "LAK") === code
                              ? "bg-primary text-primary-foreground"
                              : "bg-background text-muted-foreground hover:bg-muted",
                          )}
                          onClick={() =>
                            updateCurrency.mutate({ currency: code })
                          }
                        >
                          {code}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <Badge variant="outline">
                      {document.currency ?? "LAK"}
                    </Badge>
                  )}
                  {document.status === "draft" && canIssueDocument && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setIssueDocumentTarget(document)}
                    >
                      <FileTextIcon className="size-4" />
                      ອອກເອກະສານ
                    </Button>
                  )}
                  {document.status === "issued" &&
                    canIssueDocument &&
                    !documents.some(
                      (child) =>
                        child.parentDocumentId === document.id &&
                        (child.status === "issued" ||
                          child.status === "partial" ||
                          child.status === "paid"),
                    ) && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={reopenDocument.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            title: "ຍ້ອນກັບເປັນຮ່າງ",
                            description:
                              "ເອກະສານຈະກັບໄປສະຖານະຮ່າງເພື່ອແກ້ໄຂລາຍການໄດ້ອີກ — ເລກທີ່ເອກະສານຈະຄົງເດີມ",
                            actionText: "ຍ້ອນກັບ",
                          });
                          if (ok) {
                            closePricingEditors();
                            reopenDocument.mutate(document.id);
                          }
                        }}
                      >
                        <RefreshCcwIcon className="size-4" />
                        {reopenDocument.isPending
                          ? "ກຳລັງຍ້ອນກັບ..."
                          : "ຍ້ອນກັບເປັນຮ່າງ"}
                      </Button>
                    )}
                  {document.status !== "voided" && (
                    <PerformanceBrandPrintMenu
                      className="h-8 gap-1 px-2"
                      preview={document.status === "draft"}
                      onBeforePrint={savePendingDocumentTaxChanges}
                      signatureOptions={printSignatureOptions}
                      buildPrintUrl={(lang, options) =>
                        performanceApi.documents.printUrl(
                          document.id,
                          lang,
                          options,
                        )
                      }
                    />
                  )}
                  {document.status !== "voided" &&
                    !documents.some(
                      (child) =>
                        child.parentDocumentId === document.id &&
                        (child.status === "issued" ||
                          child.status === "partial" ||
                          child.status === "paid"),
                    ) &&
                    canVoidDocument && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:bg-destructive/10"
                        disabled={voidDocument.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            title: "ຍົກເລີກເອກະສານ",
                            description: "ເອກະສານນີ້ຈະຖືກປ່ຽນເປັນສະຖານະຍົກເລີກ",
                            actionText: "ຍົກເລີກ",
                            ActionProps: { variant: "destructive" },
                          });
                          if (ok) {
                            voidDocument.mutate({
                              documentId: document.id,
                              reason: "ຍົກເລີກຈາກໜ້າລາຍລະອຽດງານ",
                            });
                          }
                        }}
                      >
                        ຍົກເລີກ
                      </Button>
                    )}
                  {document.status === "voided" && canDeleteDocument && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:bg-destructive/10"
                      disabled={deleteDocument.isPending}
                      onClick={async () => {
                        const ok = await confirm({
                          title: "ລຶບເອກະສານ",
                          description: "ເອກະສານທີ່ຍົກເລີກແລ້ວຈະຖືກລຶບຖາວອນ — ກູ້ຄືນບໍ່ໄດ້",
                          actionText: "ລຶບ",
                          ActionProps: { variant: "destructive" },
                        });
                        if (!ok) return;
                        const deletedId = document.id;
                        await deleteDocument.mutateAsync(deletedId);
                        const remaining = documents.filter(
                          (item) => item.id !== deletedId,
                        );
                        setSelectedDocumentId(
                          pickDefaultDocumentId(remaining) ??
                            remaining[0]?.id ??
                            null,
                        );
                        closePricingEditors();
                      }}
                    >
                      <TrashIcon className="size-4" />
                      ລຶບ
                    </Button>
                  )}
                  {editableDocument && canCreatePricing && (
                    <Button
                      size="sm"
                      variant={isManualBilling ? "default" : "outline"}
                      onClick={() => {
                        setEditingLine(null);
                        setManualLineDialog({ open: true, mode: "create" });
                      }}
                    >
                      <PlusIcon className="size-4" />
                      ເພີ່ມລາຍການ
                    </Button>
                  )}
                  {editableDocument && !isManualBilling && canUpdatePricing && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        const result = await syncPricing.mutateAsync(
                          editableDocument.id,
                        );
                        setSyncWarnings(result.warnings);
                      }}
                      disabled={syncPricing.isPending}
                    >
                      <RefreshCcwIcon className="size-4" />
                      {syncPricing.isPending ? "ກຳລັງຊິງກ໌..." : "ຊິງກ໌ລາຄາ"}
                    </Button>
                  )}
                </div>
              </div>

              {syncWarnings.length > 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 text-xs">
                  <p className="mb-1 font-medium">ຄຳເຕືອນຈາກການຊິງກ໌</p>
                  <ul className="list-disc space-y-0.5 pl-4">
                    {syncWarnings.map((warning) => (
                      <li key={`${warning.assignmentId}-${warning.code}`}>
                        {warning.message}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {!isManualBilling && assignments.length > 0 ? (
                <RoleFeePricingBreakdownPanel
                  assignments={assignments}
                  eventShows={eventShows}
                />
              ) : null}

              {paymentTermsDocument && (
                <DocumentPaymentTermsEditor
                  eventId={eventId}
                  document={paymentTermsDocument}
                  canEdit={canCreateDocument}
                />
              )}

              {bankAccountDocument && (
                <DocumentBankAccountEditor
                  eventId={eventId}
                  document={bankAccountDocument}
                  canEdit={canCreateDocument}
                />
              )}

              <ManualLineDialog
                state={manualLineDialog}
                manualBilling={isManualBilling}
                currency={editableDocument?.currency ?? "LAK"}
                submitting={
                  manualLineDialog.open && manualLineDialog.mode === "create"
                    ? createManualLine.isPending
                    : updateManualLine.isPending
                }
                onOpenChange={(open) => {
                  if (!open) setManualLineDialog({ open: false });
                }}
                onSubmit={async (values) => {
                  if (
                    manualLineDialog.open &&
                    manualLineDialog.mode === "edit"
                  ) {
                    await updateManualLine.mutateAsync({
                      lineId: manualLineDialog.line.id,
                      input: values,
                    });
                    setManualLineDialog({ open: false });
                    return;
                  }
                  await createManualLine.mutateAsync(values);
                  setManualLineDialog({ open: false });
                }}
              />

              {editingLine &&
                editableDocument &&
                editingLine.lineType === "auto" && (
                  <AutoLinePricingForm
                    line={editingLine}
                    currency={editableDocument.currency ?? "LAK"}
                    submitting={updateManualLine.isPending}
                    onCancel={() => setEditingLine(null)}
                    onSubmit={async (values) => {
                      await updateManualLine.mutateAsync({
                        lineId: editingLine.id,
                        input: values,
                      });
                      setEditingLine(null);
                    }}
                  />
                )}

              {document.status !== "voided" && (
                <DocumentPrintSignatureOptionsPanel
                  documentType={document.documentType}
                  value={printSignatureOptions}
                  onChange={setPrintSignatureOptions}
                  className="rounded-lg border bg-background px-4 py-3"
                />
              )}

              <div className="overflow-x-auto rounded-lg border">
                {showInstallmentLineItems ? (
                  installmentSummaryQuery.isLoading ? (
                    <div className="px-4 py-10 text-center text-muted-foreground text-sm">
                      ກຳລັງໂຫຼດແຜນງວດ...
                    </div>
                  ) : (
                    <div className="p-4">
                      <DocumentInstallmentLinesTable
                        installments={installmentLineItems}
                        currency={document.currency ?? "LAK"}
                        showStatus={document.documentType === "invoice"}
                        totalLabel={
                          document.documentType === "receipt"
                            ? "ຍອດຮັບຄັ້ງນີ້"
                            : document.taxEnabled
                              ? "ຍອດລວມແຜນງວດ (ບໍ່ລວມ VAT)"
                              : "ຍອດລວມແຜນງວດ"
                        }
                        planTargetAmount={installmentPlanTargetMinor}
                      />
                    </div>
                  )
                ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="px-4 py-2.5 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ລາຍການ
                      </th>
                      <th className="px-4 py-2.5 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ແຫຼ່ງ
                      </th>
                      <th className="px-4 py-2.5 text-right font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ຈຳນວນ
                      </th>
                      <th className="px-4 py-2.5 text-right font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ລາຄາ
                      </th>
                      <th className="px-4 py-2.5 text-right font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ລວມ
                      </th>
                      <th className="px-4 py-2.5 text-right font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ຈັດການ
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {lines.length === 0 ? (
                      <tr>
                        <td
                          className="px-4 py-10 text-center text-muted-foreground"
                          colSpan={6}
                        >
                          {isManualBilling
                            ? "ຍັງບໍ່ມີລາຍການລາຄາ — ເພີ່ມລາຍການດ້ວຍມື"
                            : "ຍັງບໍ່ມີລາຍການລາຄາ"}
                          {editableDocument ? " — ກົດເພີ່ມລາຍການເພື່ອເລີ່ມ" : null}
                        </td>
                      </tr>
                    ) : (
                      lines.map((line) => (
                        <Fragment key={line.id}>
                          <tr className="hover:bg-muted/20">
                            <td className="min-w-[200px] px-4 py-3">
                              <div className="font-medium">
                                {line.description}
                              </div>
                              {line.unitPrice === 0 &&
                                line.lineType === "auto" && (
                                  <div className="mt-1 flex items-center gap-1 text-amber-700 text-xs">
                                    <AlertTriangleIcon className="size-3.5" />
                                    ຍັງບໍ່ມີ rate
                                  </div>
                                )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                <Badge
                                  variant={
                                    line.lineType === "auto"
                                      ? "secondary"
                                      : "outline"
                                  }
                                >
                                  {LINE_TYPE_LABELS[line.lineType]}
                                </Badge>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-right">
                              {line.quantity} {displayUnit(line.unit)}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-right">
                              {formatMoney(
                                line.unitPrice,
                                document.currency ?? "LAK",
                              )}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-right font-semibold">
                              {formatMoney(
                                line.subtotal,
                                document.currency ?? "LAK",
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {editableDocument && canUpdatePricing ? (
                                <div className="flex justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 gap-1 px-2"
                                    onClick={() => {
                                      if (line.lineType === "manual") {
                                        setEditingLine(null);
                                        setManualLineDialog({
                                          open: true,
                                          mode: "edit",
                                          line,
                                        });
                                        return;
                                      }
                                      setManualLineDialog({ open: false });
                                      setEditingLine(line);
                                    }}
                                  >
                                    <PencilIcon className="size-3.5" />
                                    <span className="text-xs">ແກ້ໄຂ</span>
                                  </Button>
                                  {line.lineType === "manual" &&
                                    canDeletePricing && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 gap-1 px-2 text-destructive hover:bg-destructive/10"
                                        disabled={deleteManualLine.isPending}
                                        onClick={async () => {
                                          const ok = await confirm({
                                            title: "ລຶບລາຍການເອງ",
                                            description:
                                              "ລາຍການລາຄາທີ່ເພີ່ມເອງນີ້ຈະຖືກລຶບ",
                                            actionText: "ລຶບ",
                                            ActionProps: {
                                              variant: "destructive",
                                            },
                                          });
                                          if (ok) {
                                            toast.promise(
                                              deleteManualLine.run(line.id),
                                              {
                                                loading: "ກຳລັງລຶບ...",
                                                success: "ລຶບສຳເລັດ",
                                                error: "ລຶບບໍ່ສຳເລັດ",
                                              },
                                            );
                                          }
                                        }}
                                      >
                                        <TrashIcon className="size-3.5" />
                                        <span className="text-xs">ລຶບ</span>
                                      </Button>
                                    )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-xs">
                                  —
                                </span>
                              )}
                            </td>
                          </tr>
                        </Fragment>
                      ))
                    )}
                  </tbody>
                </table>
                )}
              </div>

              {document &&
                (document.documentType === "quotation" ||
                  document.documentType === "invoice") &&
                document.status !== "voided" && (
                  <DocumentTaxEditor
                    ref={documentTaxEditorRef}
                    eventId={eventId}
                    document={document}
                    canEdit={canCreateDocument && document.status !== "voided"}
                  />
                )}

              {document?.documentType === "quotation" &&
                document.status !== "voided" && (
                  <QuotationInstallmentPlanPanel
                    eventId={eventId}
                    quotation={document}
                    onInvoiceCreated={selectDocument}
                  />
                )}

              {showInvoicePayments && selectedSummary && (
                <InvoicePaymentPanel
                  eventId={eventId}
                  invoice={selectedSummary}
                  printSignatureOptions={printSignatureOptions}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {issuedQuotation && (
        <CreateInvoiceFromQuotationDialog
          open={createInvoiceOpen}
          onOpenChange={setCreateInvoiceOpen}
          eventId={eventId}
          quotationId={issuedQuotation.id}
          currency={issuedQuotation.currency ?? "LAK"}
          onCreated={(documentId) => selectDocument(documentId)}
        />
      )}

      <IssueDocumentDialog
        eventId={eventId}
        document={issueDocumentTarget}
        open={issueDocumentTarget !== null}
        onOpenChange={(open) => {
          if (!open) setIssueDocumentTarget(null);
        }}
        onBeforeIssue={async () => {
          await savePendingDocumentTaxChanges();
          closePricingEditors();
        }}
      />
    </section>
  );
}
